import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Session, SessionClaims } from './shared-constants';
import { from, Observable, of, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

// The "no session yet" placeholder. Previously imported as `initialState` from
// the app's session reducer; inlined here so the shared service does not depend
// on app-local NgRx code. Same object literal, same behaviour.
const initialState: any = {
	isLoaded: false,
	isAnonymous: null,
	emailVerified: null,
	lastLoginAt: null,
	uid: null,
	apiKey: null,
	email: '',
	displayName: '',
	createdAt: '',
	phoneNumber: '',
	photoURL: '',
	claims: {
		admin: false,
		accessLevel: 0
	}
};

@Injectable()
export class SessionService {
	_auth: Observable<Session | null>;

	get auth() {
		return this._auth;
	}

	subscription: Subscription;

	constructor(private afAuth: AngularFireAuth) {
		this._auth = this.afAuth.user.pipe(
			switchMap(u =>
				of(u).pipe(
					switchMap(state =>
						!state
							? of(initialState)
							: afAuth.user.pipe(
									switchMap(u =>
										from(u.getIdTokenResult()).pipe(
											map((idTokenResult: any) => {
												if (state) {
													const session = state.toJSON() as Session;
													return { ...session, claims: idTokenResult.claims as SessionClaims };
												}
												return initialState;
											})
										)
									)
								)
					)
				)
			)
		);
	}

	reload(): Observable<Session> {
		return this.afAuth.user.pipe(
			take(1),
			switchMap(u =>
				from(u.reload()).pipe(
					switchMap(_state =>
						from(u.getIdTokenResult()).pipe(
							map((idTokenResult: any) => {
								const session = u.toJSON() as Session;
								return { ...session, claims: idTokenResult.claims as SessionClaims };
							})
						)
					)
				)
			)
		);
	}

	start() {
		return this._auth;
	}
}
